// Notifications tab: the signed-in staff user's own notification switches
// (BL-1230; per-notification preferences, on or off by default, BL-2469).
//
// BEFORE BL-1230 this tab rendered two hardcoded Email/SMS toggles seeded from
// `useState`. They were never fetched and never saved — the modal rendered no
// Save button on this tab at all — so flipping one and reopening the modal
// restored the defaults. They are deleted rather than kept, because a tab where
// one toggle persists and two silently don't is worse than one that is uniformly
// cosmetic: it invites the user to trust all three.
//
// What replaces them is server-driven. The route returns every toggle, each with
// its current setting, and marks `locked` (with the reason) the ones this user's
// roles at the active tenant don't make them eligible for. This component
// renders exactly what it is handed. It holds no role logic: a role changes
// what staff can do, never where things are, so a locked row stays in place with
// its padlock and a reason on hover, press or focus (BUG-2272, rule #46) — and a
// future switchable event appears here with no change to this file.
//
// BL-2588 adds one local row above the list: the notification chime's Sound
// switch. It lives on this device, saves at once and is not part of Save.
'use client';

import { clsx } from 'clsx';
import { Lock } from 'lucide-react';
import { useAttemptReveal, useNotificationSound } from '@auctogon/ui/web';

import { useAuth } from '../../auth/AuthProvider';
import Toggle from '../../settings-v2/Toggle';

import type { StaffNotificationPreferenceDto } from '@auctogon/core-admin/schemas/responses/staff-notification-preferences';

/** Props for the notifications preferences tab. */
export interface NotificationsTabContentProps {
  /** Every toggle for this user at the active tenant, the ones their roles can't switch marked locked. */
  preferences: StaffNotificationPreferenceDto[];
  /** True while the initial fetch is in flight. */
  isLoading: boolean;
  /**
   * Load/save failure message, rendered inline above the list.
   *
   * Inline, never behind a modal or a toast: the user is already IN a modal, and
   * an error that closes the surface it describes is unactionable
   * (error-handling-chain.md).
   */
  error: string | null;
  /** Flip one toggle. The parent holds the pending state until Save. */
  onToggle: (eventType: string, channel: string, enabled: boolean) => void;
}

/**
 * Renders the staff user's own notification switches, one row per notification
 * with its Email switch.
 *
 * @param props - the eligible preferences, load/error state, and toggle handler.
 */
export default function NotificationsTabContent({
  preferences,
  isLoading,
  error,
  onToggle,
}: NotificationsTabContentProps) {
  const { user } = useAuth();
  const sound = useNotificationSound(user?.id ?? null);
  const soundRow = (
    <ToggleRow
      testId="notification-sound"
      title="Sound"
      enabled={sound.enabled}
      onToggle={() => sound.setEnabled(!sound.enabled)}
    />
  );

  if (isLoading) {
    return (
      <div className="space-y-density-group">
        {soundRow}
        <p className="text-body text-gray-500" role="status">
          Loading your notification settings…
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-density-group">
      {soundRow}
      {error && (
        <p role="alert" className="text-body text-red-600">
          {error}
        </p>
      )}

      {preferences.length > 0 ? (
        <>
          {/* Every row's switch is its Email channel today (the only channel a
              staff switch covers), so one column header names it (BL-2469 AC 1). */}
          <div
            className="flex justify-end px-4 text-caption text-gray-600"
            data-testid="notification-preferences-channel-header"
          >
            Email
          </div>
          {preferences.map((preference) => (
            <ToggleRow
              key={`${preference.eventType}:${preference.channel}`}
              testId={`notification-preference-${preference.eventType}-${preference.channel}`}
              title={preference.label}
              description={preference.description}
              enabled={preference.enabled}
              lockReason={preference.locked ? preference.lockReason : undefined}
              onToggle={() =>
                onToggle(preference.eventType, preference.channel, !preference.enabled)
              }
            />
          ))}
        </>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */

interface ToggleRowProps {
  /** Automation handle for the row, keyed on its (event, channel) identity. */
  testId: string;
  title: string;
  /** Shown only when the catalogue gives the row one (rule #44). */
  description?: string;
  enabled: boolean;
  /** Set when the viewer's roles can't switch this row: it renders locked, with this reason (rule #46). */
  lockReason?: string;
  onToggle: () => void;
}

/**
 * A labelled switch row: one notification's Email channel, or the device's Sound switch.
 * Locked, the switch is disabled behind a padlock and the reason shows on hover (the
 * row's tooltip) and on press or focus (BL-2398's attempt reveal, rule #44 case 3).
 */
function ToggleRow({ testId, title, description, enabled, lockReason, onToggle }: ToggleRowProps) {
  const locked = lockReason != null;
  const reasonId = `${testId}-lock-reason`;
  const attempt = useAttemptReveal({ locked, hintId: reasonId, testId });
  return (
    <div
      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
      data-testid={testId}
      title={lockReason}
    >
      <div>
        <p className="flex items-center gap-density-cluster font-medium text-primary">
          {title}
          {locked ? <Lock size={14} aria-hidden data-testid={`${testId}-lock`} /> : null}
        </p>
        {description ? <p className="text-body text-gray-500">{description}</p> : null}
        {/* gray-500 on the row's gray-50: 4.63:1 text (needs 4.5) */}
        {locked ? (
          <p
            id={reasonId}
            className={clsx('text-caption text-gray-500', !attempt.revealed && 'sr-only')}
          >
            {lockReason}
          </p>
        ) : null}
      </div>
      {attempt.wrap(
        <Toggle
          checked={enabled}
          onChange={onToggle}
          disabled={locked}
          ariaLabel={title}
          testId={`${testId}-switch`}
        />,
      )}
    </div>
  );
}
